/**
 * E30 · Portal Build Freshness — what a tab knows about the build it is running.
 *
 * ─── THE PROBLEM ────────────────────────────────────────────────────────────────────────────────
 * The portal has never told anyone that it changed. A therapist keeps a tab open for days; a deploy
 * lands; they keep using the build they loaded on Monday and report bugs we fixed on Tuesday.
 *
 * ─── THE WHOLE MECHANISM ────────────────────────────────────────────────────────────────────────
 * `build-real` stamps `build/version.json` (`{ buildId, builtAt }`) AND bakes the same id into the
 * bundle as `REACT_APP_PORTAL_BUILD_ID`. Firebase Hosting serves that one file with
 * `Cache-Control: no-store` (firebase.json), so a tab can ask what is being served RIGHT NOW and
 * compare it with what it is running. No server of ours is involved and nothing is appended to the
 * event log: all five E30 events are `scope: client / sync: never`, because a tab's observation
 * about itself is not a domain fact (registry `A-no-domain-fact`).
 *
 * ─── EQUALITY, NEVER ORDER ──────────────────────────────────────────────────────────────────────
 * The build id is a short git sha, and a sha has no order — `stale` is `served !== baked`, not
 * `served > baked` (registry `A-build-id-is-a-git-sha`). This is why the read model's field is
 * `stale: Boolean` and not mobile's `isBelowRequired`.
 *
 * ─── FAIL-QUIET ─────────────────────────────────────────────────────────────────────────────────
 * A fetch that errors, 404s or answers without a `buildId` leaves the pending check OPEN and the
 * next check retries (board: 'Portal build · compare served id to baked id'). A hosting hiccup, a
 * captive-portal login page or the CRA dev server's index.html fallback must never show the prompt,
 * so `evaluatePortalBuildFreshness` returns the PREVIOUS state unchanged — the same object, so
 * React re-renders nothing.
 *
 * Everything here is framework-free on purpose: the hook (`hooks/usePortalBuildFreshness`) only
 * binds these functions to React, and the unit specs drive them with a fake document and fake
 * timers (`portalBuild.test.ts`, one test per board GWT).
 */

/**
 * The id baked into THIS bundle, inlined by webpack's DefinePlugin at build time (CRA only inlines
 * `REACT_APP_*`, and only when the whole `process.env.X` expression is written out literally).
 *
 * `dev` is what `yarn workspace @cc/portal start` runs under: a dev server has no stamped bundle,
 * and a dev server also has no `/version.json` to serve, so the check answers nothing and the
 * prompt never appears — which is the correct behaviour for a build that is rebuilt on every save.
 */
export const PORTAL_BUILD_ID: string =
  process.env.REACT_APP_PORTAL_BUILD_ID || 'dev';

/** The stamped file `build-real` writes and Firebase Hosting serves uncached. */
export const VERSION_JSON_URL = '/version.json';

/**
 * ~15 minutes while the tab is open, PAUSED while it is hidden (registry `interval-length`, ruled
 * as recommended 2026-09-13). A tab open all day makes ~32 requests of a ~60-byte file; pausing
 * while hidden removes the overnight-tab case entirely, and the foreground check covers the return.
 */
export const PORTAL_BUILD_CHECK_INTERVAL_MS = 15 * 60 * 1000;

/**
 * `PortalBuildPublished` — Firebase Hosting's own fact, producerless by the translation law
 * (TRANS-1). We never model the vendor's logic, only the answer it gives us.
 */
export type PortalBuildPublished = {
  deployedBuildId: string;
  builtAt: string | null;
};

/** The `PortalBuildFreshness` read model: what this tab concluded about its own build. */
export type PortalBuildFreshness = {
  bakedBuildId: string;
  deployedBuildId: string | null;
  stale: boolean;
  checkedAt: string | null;
};

/** No check has completed yet — `checkedAt` null, and the prompt only ever appears on a positive answer. */
export const initialPortalBuildFreshness = (
  bakedBuildId: string = PORTAL_BUILD_ID,
): PortalBuildFreshness => ({
  bakedBuildId,
  deployedBuildId: null,
  stale: false,
  checkedAt: null,
});

/**
 * `TranslatePortalBuild` — OUR fact, recorded from the vendor's answer. Pure.
 *
 * Returns `previous` BY REFERENCE when there is nothing to conclude, so a failed check is
 * invisible to React and cannot re-open a dismissed prompt.
 */
export const evaluatePortalBuildFreshness = (
  previous: PortalBuildFreshness,
  published: PortalBuildPublished | null,
  checkedAt: string,
): PortalBuildFreshness => {
  if (!published || !published.deployedBuildId) {
    return previous;
  }
  return {
    bakedBuildId: previous.bakedBuildId,
    deployedBuildId: published.deployedBuildId,
    stale: published.deployedBuildId !== previous.bakedBuildId,
    checkedAt,
  };
};

/**
 * `{ buildId, builtAt }` → the external fact, or `null` for anything else.
 *
 * The dev server and the SPA rewrite both answer `index.html` for an unknown path, and a captive
 * portal answers its own login page; `response.json()` throws on those, and a body without a
 * string `buildId` is discarded here. Both roads end at fail-quiet.
 */
export const readPortalBuildPublished = (
  body: unknown,
): PortalBuildPublished | null => {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const { buildId, builtAt } = body as { buildId?: unknown; builtAt?: unknown };
  if (typeof buildId !== 'string' || buildId.length === 0) {
    return null;
  }
  return {
    deployedBuildId: buildId,
    builtAt: typeof builtAt === 'string' ? builtAt : null,
  };
};

/**
 * `CheckPortalBuild` — ask Firebase Hosting what it is serving. Never throws.
 *
 * `cache: 'no-store'` belts the braces of the `firebase.json` header: the header is what stops a
 * CDN and a proxy caching the file, this is what stops the BROWSER doing it, and a check that reads
 * its own cached answer for ever is the one failure mode that would make the whole epic silent.
 */
export const fetchPortalBuildPublished = async (
  fetchImpl: typeof fetch = fetch,
): Promise<PortalBuildPublished | null> => {
  try {
    const response = await fetchImpl(VERSION_JSON_URL, { cache: 'no-store' });
    if (!response.ok) {
      return null;
    }
    return readPortalBuildPublished(await response.json());
  } catch {
    return null;
  }
};

/** The browser surface the watcher needs — named so a spec can hand it a fake. */
export type PortalBuildWatchHost = {
  visibilityState: string;
  addEventListener: (type: 'visibilitychange', listener: () => void) => void;
  removeEventListener: (type: 'visibilitychange', listener: () => void) => void;
  setInterval: (handler: () => void, ms: number) => number;
  clearInterval: (handle: number) => void;
};

const browserWatchHost = (): PortalBuildWatchHost => ({
  get visibilityState() {
    return document.visibilityState as string;
  },
  addEventListener: (type, listener) => document.addEventListener(type, listener),
  removeEventListener: (type, listener) =>
    document.removeEventListener(type, listener),
  setInterval: (handler, ms) => window.setInterval(handler, ms),
  clearInterval: (handle) => window.clearInterval(handle),
});

/**
 * The trigger half of `E30 · CheckPortalBuild`: the browser fact `PortalTabForegrounded`
 * (`visibilitychange → visible`) fills the check list, and the interval works the SAME list while
 * the tab stays open — one gear, two triggers, which is why they are one slice and not two.
 *
 * Returns the unsubscribe. The interval is started only while the tab is visible and cleared the
 * moment it is hidden.
 */
export const watchPortalBuild = (
  check: () => void,
  host: PortalBuildWatchHost = browserWatchHost(),
  intervalMs: number = PORTAL_BUILD_CHECK_INTERVAL_MS,
): (() => void) => {
  let timer: number | null = null;

  const stopTicking = () => {
    if (timer !== null) {
      host.clearInterval(timer);
      timer = null;
    }
  };

  const startTicking = () => {
    if (timer === null) {
      timer = host.setInterval(check, intervalMs);
    }
  };

  const onVisibilityChange = () => {
    if (host.visibilityState === 'visible') {
      check();
      startTicking();
    } else {
      stopTicking();
    }
  };

  host.addEventListener('visibilitychange', onVisibilityChange);

  if (host.visibilityState === 'visible') {
    check();
    startTicking();
  }

  return () => {
    host.removeEventListener('visibilitychange', onVisibilityChange);
    stopTicking();
  };
};

/**
 * `ReloadPortal` → `PortalReloadRequested`. Client-only: the tab reloads and nothing is appended.
 *
 * v1 is the banner ONLY, so this is reachable exclusively from the person's own click and a dirty
 * form is safe by construction (registry `never-while-dirty`). The automatic caller is the
 * `Planned` slice `E30 · AutoReloadOnRouteChange`, with the dirty-form guard as its second GWT.
 */
export const reloadPortal = (): void => {
  window.location.reload();
};
